// Create energy initiative
// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import CreateInitiativeForm from '@/features/energyxplorer/forms/CreateInitiativeForm';
// components
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary.tsx';

export const Route = createFileRoute(
  '/$accountId/_user/energyxplorer/deepdive/$energyGroupId/energy-initiatives/create',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: {
    crumb: 'common:actions.create',
    hideEnergyAnalysisAppBar: true,
  },
});

function RouteComponent() {
  const { t } = useTranslation(['energyxplorer-initiatives-tab', 'forms']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { energyGroupId } = useParams({ strict: false });

  const { data: energyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );

  const handleSuccess = () => {
    dispatch(
      showSnackbar({
        message: t('common.createSuccess', { ns: 'forms' }),
        severity: 'success',
      }),
    );
    navigate({
      to: '../',
      search: (prev: Record<string, string>) => ({ ...prev }),
    });
  };

  const handleCancel = () => {
    navigate({
      to: '../',
      search: (prev: Record<string, string>) => ({ ...prev }),
    });
  };

  return (
    <PageContainer
      maxWidth="xl"
      sx={{ py: 2 }}
      data-testid="create-initiative-page"
    >
      <PageHeader title={t('dialogs.create.title')} to="../" />
      <CreateInitiativeForm
        energyGroup={energyGroup}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </PageContainer>
  );
}
